import React, {
  ReactElement,
  MutableRefObject,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import MultipleSelectDropDown, {
  OptionType,
} from '../DropDowns/MultipleSelectDropDown';
import SingleSelectDropDown from '../DropDowns/SingleSelectDropDown';
import TeamMemberMultiSelect from '../DropDowns/TeamMemberMultiSelect';
import './BillCustomField.scss';
import CloseIcon from '../../../components/svg/CloseIcon';
import DropDownButton from '../Buttons/DropDownButton';
import './BillCustomField.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import {
  CustomFieldFilterValue,
  CustomFieldRule,
} from '@enview/interface/types/BillSearch';
import CustomField, { CustomFieldTypeEnum } from '@enview/interface/types/CustomFields';
import { TeamAPI } from '../../../api';

const ASSIGNEE_FIELD_TITLE = 'Assignee';

const CUSTOM_FIELD_RULE_OPTIONS: OptionType<string>[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
  { value: 'equals', label: 'Is exactly' },
];

const splitDropdownValues = (text: string | undefined): string[] =>
  text
    ? text
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    : [];

interface CustomFieldFilterProps extends React.HTMLAttributes<HTMLDivElement> {
  customFields: CustomField[];
  teamId: number;
  filterValue: CustomFieldFilterValue;
  onSave: (value: CustomFieldFilterValue) => void;
  clearAllRef?: MutableRefObject<((disabled: boolean) => void) | undefined>;
}

export const CustomFieldFilter = (props: CustomFieldFilterProps): ReactElement => {
  const {
    customFields,
    teamId,
    filterValue,
    onSave,
    clearAllRef,
    className,
    ...divProps
  } = props;

  const { field: initialField, rule: initialRule, text: initialText } = filterValue;

  const [selectedField, setSelectedField] = useState<string>(initialField);
  const [selectedRule, setSelectedRule] = useState<CustomFieldRule>(initialRule);
  const [textValue, setTextValue] = useState<string>(initialText ?? '');
  const [selectedDropdownValues, setSelectedDropdownValues] = useState<string[]>(
    splitDropdownValues(initialText),
  );

  const [displayed, setDisplayed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const customFieldOptions: OptionType<string>[] = customFields.map((field) => ({
    value: field.title,
    label: field.title,
  }));

  const selectedFieldMeta = customFields.find((field) => field.title === selectedField);
  const isDropdownField = selectedFieldMeta?.type === CustomFieldTypeEnum.Dropdown;
  const isAssigneeField = selectedFieldMeta?.title === ASSIGNEE_FIELD_TITLE;

  const equalsRuleLabel = selectedFieldMeta?.multiSelect ? 'Is any of' : 'Is exactly';

  const ruleOptions = isDropdownField
    ? CUSTOM_FIELD_RULE_OPTIONS.filter((option) => option.value === 'equals').map(
        (option) => ({ ...option, label: equalsRuleLabel }),
      )
    : CUSTOM_FIELD_RULE_OPTIONS.filter((option) => option.value !== 'equals');

  const showDropdownValuePicker = isDropdownField && selectedRule === 'equals';
  const shouldShowTextInput =
    !showDropdownValuePicker &&
    (selectedRule === 'contains' || selectedRule === 'does_not_contain');

  const { data: teamMembers = [] } = TeamAPI.endpoints.getTeamMembers.useQuery(
    isAssigneeField ? teamId : skipToken,
  );

  const dropdownValueOptions: OptionType<string>[] = isAssigneeField
    ? teamMembers.map((member) => ({
        value: String(member.organizationUserId),
        label: member.name || member.email || String(member.organizationUserId),
      }))
    : (selectedFieldMeta?.options ?? []).map((option) => ({
        value: option,
        label: option,
      }));

  const hasActiveFilter =
    !!selectedField &&
    (selectedRule === 'is_empty' ||
      selectedRule === 'is_not_empty' ||
      (shouldShowTextInput && !!textValue.trim()) ||
      (showDropdownValuePicker && selectedDropdownValues.length > 0));

  const handleSave = () => {
    if (!selectedField) return;

    let text: string | undefined;
    if (showDropdownValuePicker) {
      text =
        selectedDropdownValues.length > 0
          ? selectedDropdownValues.join(',')
          : undefined;
    } else if (shouldShowTextInput) {
      text = textValue;
    }

    onSave({
      field: selectedField,
      rule: selectedRule,
      text,
    });

    setDisplayed(false);
  };

  const handleFieldSelect = (fieldTitle: string) => {
    setSelectedField(fieldTitle);
    const newFieldMeta = customFields.find((field) => field.title === fieldTitle);
    const newFieldIsDropdown = newFieldMeta?.type === CustomFieldTypeEnum.Dropdown;
    if (newFieldIsDropdown && selectedRule !== 'equals') {
      // "Is exactly" is the only rule available for dropdown fields
      setSelectedRule('equals');
    } else if (!newFieldIsDropdown && selectedRule === 'equals') {
      setSelectedRule('contains');
    }
    setTextValue('');
    setSelectedDropdownValues([]);
  };

  const clearAllFilters = useCallback((disabled: boolean) => {
    if (disabled) return;
    setSelectedField('');
    setSelectedRule('contains');
    setTextValue('');
    setSelectedDropdownValues([]);

    onSave({
      field: '',
      rule: 'contains',
      text: undefined,
    });
  }, []);

  useEffect(() => {
    if (clearAllRef) {
      clearAllRef.current = clearAllFilters;
    }
  }, [clearAllRef, clearAllFilters]);

  useEffect(() => {
    setSelectedField(initialField);
    setSelectedRule(initialRule);
    setTextValue(initialText ?? '');
    setSelectedDropdownValues(splitDropdownValues(initialText));
  }, [initialField, initialRule, initialText]);

  const saveDisabled =
    !selectedField ||
    !selectedRule ||
    (shouldShowTextInput && !textValue.trim()) ||
    (showDropdownValuePicker && selectedDropdownValues.length === 0);

  const handleClickOutside = useCallback((e: MouseEvent): void => {
    if (ref.current && ref.current.contains(e.target as Element)) {
      return;
    }
    setDisplayed(false);
  }, []);

  useEffect(() => {
    if (displayed) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [displayed, handleClickOutside]);

  const showClass = displayed ? ' show' : '';

  return (
    <div
      {...divProps}
      className={`dropdown custom-field-filter ${className ?? ''}`}
      ref={ref}
    >
      <DropDownButton
        dropdownDisplayed={displayed}
        id="custom-field-filter-dropdown-button"
        label="Custom Field Filter"
        onClick={() => setDisplayed((prev) => !prev)}
        selectedCount={hasActiveFilter ? 1 : 0}
      />

      <div
        aria-labelledby="custom-field-filter-dropdown-button"
        className={`dropdown-menu multi-select custom-field-filter-menu${showClass}`}
      >
        <div className="d-flex flex-row">
          <div className="title">Custom Field Filter</div>
          <CloseIcon
            className="align-self-center"
            onClick={() => {
              setDisplayed(false);
            }}
          />
        </div>

        <div className="filter-body my-3">
          <div className="filter-row d-flex align-items-center mb-3 mt-2">
            <span className="filter-label mr-2">Where</span>

            <div className="filter-control flex-grow-1 mr-2" style={{ flexBasis: 0 }}>
              <SingleSelectDropDown
                defaultLabel="Custom Field"
                id="custom-field-select"
                onSelect={(option) => handleFieldSelect(option.value)}
                options={customFieldOptions}
                selectedValue={selectedField}
              />
            </div>

            <div className="filter-control flex-grow-1" style={{ flexBasis: 0 }}>
              <SingleSelectDropDown
                defaultLabel="Contains"
                disabled={ruleOptions.length <= 1}
                id="custom-field-rule-select"
                onSelect={(option) => setSelectedRule(option.value as CustomFieldRule)}
                options={ruleOptions}
                selectedValue={selectedRule}
              />
            </div>
          </div>

          <div className="filter-row">
            {showDropdownValuePicker && isAssigneeField && (
              <div className="filter-control">
                <TeamMemberMultiSelect
                  members={teamMembers}
                  onChange={setSelectedDropdownValues}
                  selectedValues={selectedDropdownValues}
                />
              </div>
            )}

            {showDropdownValuePicker &&
              !isAssigneeField &&
              selectedFieldMeta?.multiSelect && (
                <div className="filter-control">
                  <MultipleSelectDropDown
                    currentSelectedValues={selectedDropdownValues}
                    onChange={setSelectedDropdownValues}
                    options={dropdownValueOptions}
                    title="Select values"
                  />
                </div>
              )}

            {showDropdownValuePicker && !selectedFieldMeta?.multiSelect && (
              <div className="filter-control">
                <SingleSelectDropDown
                  defaultLabel="Select value"
                  id="custom-field-value-select"
                  onSelect={(option) => setSelectedDropdownValues([option.value])}
                  options={dropdownValueOptions}
                  selectedValue={selectedDropdownValues[0]}
                />
              </div>
            )}

            {shouldShowTextInput && (
              <div className="filter-control">
                <div className="input-with-icon">
                  <FontAwesomeIcon className="icon" icon={faPen} />
                  <input
                    className="filter-search-input"
                    name="name"
                    onChange={(e) => setTextValue(e.target.value)}
                    placeholder="Enter search text"
                    type="text"
                    value={textValue}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-row mt-auto">
          <button
            className="btn btn-action"
            disabled={saveDisabled}
            onClick={handleSave}
            type="button"
          >
            Save
          </button>

          <button
            className="btn btn-link"
            onClick={() => clearAllFilters(!hasActiveFilter)}
            type="button"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
};
